// pages/Contact.js
import React, { useState, useEffect } from "react";
import Header from "../components/SetNavbar";
import Footer from "../components/Footer";
import {
  Box,
  Container,
  Grid,
  Typography,
  TextField,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  Chip,
  Fade,
  Slide,
  Snackbar,
  Alert,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Divider,
  Stack,
} from "@mui/material";
import {
  ExpandMore,
  Phone,
  Email,
  LocationOn,
  Send,
  CheckCircle,
  Support,
  Message,
  Person,
  Business,
  RocketLaunch,
  Verified,
  Star,
} from "@mui/icons-material";
import Telephone from "../assets/ContactUs/tel.png";
import "../styles/Contact.css";

const ContactUs = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const faqs = [
    {
      question: "What courses do you offer?",
      answer:
        "We offer a wide range of courses including Artificial Intelligence, Microsoft Dynamics 365, JavaScript Development, Mobile & Web App Development, Cybersecurity, Agile Methodology, Cloud Computing, Data Science, DevOps, and more.",
    },
    {
      question: "Are the courses online or in-person?",
      answer:
        "We offer both online and in-person training options. Our online courses are live, interactive sessions with expert instructors, while in-person classes are available at our training centers.",
    },
    {
      question: "Do you provide certificates upon completion?",
      answer:
        "Yes, we provide industry-recognized certificates upon successful completion of our courses. These certificates are valued by employers and can boost your career prospects.",
    },
    {
      question: "What is the duration of the courses?",
      answer:
        "Course duration varies depending on the program. Most courses range from 6 to 12 weeks, with flexible scheduling options to accommodate different learning paces.",
    },
    {
      question: "Do you offer job placement assistance?",
      answer:
        "Yes, we offer career support services including resume building, interview preparation, and job placement assistance to help you land your dream job in tech.",
    },
    {
      question: "What is the refund policy?",
      answer:
        "We offer a 30-day money-back guarantee if you're not satisfied with the course. Contact our support team for more details about our refund policy.",
    },
  ];

  const contactInfo = [
    {
      icon: <Phone sx={{ fontSize: 28 }} />,
      title: "Phone",
      details: "+1 (555) 123-4567",
      sub: "Mon-Fri 9am-6pm EST",
      color: "#2BAD65",
      bgColor: "rgba(43, 173, 101, 0.12)",
    },
    {
      icon: <Email sx={{ fontSize: 28 }} />,
      title: "Email",
      details: "info@wavepalmtechub.com",
      sub: "support@wavepalmtechub.com",
      color: "#A0EF48",
      bgColor: "rgba(160, 239, 72, 0.12)",
    },
    {
      icon: <LocationOn sx={{ fontSize: 28 }} />,
      title: "Location",
      details: "123 Tech Hub Street",
      sub: "Silicon Valley, CA 94025",
      color: "#FF6B6B",
      bgColor: "rgba(255, 107, 107, 0.12)",
    },
  ];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstName.trim())
      newErrors.firstName = "First name is required";
    if (!formData.lastName.trim()) newErrors.lastName = "Last name is required";
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email";
    }
    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (!/^[\d\s-()+]+$/.test(formData.phone)) {
      newErrors.phone = "Please enter a valid phone number";
    }
    if (!formData.message.trim()) newErrors.message = "Message is required";
    if (!formData.subject.trim()) newErrors.subject = "Subject is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      setIsSubmitting(true);
      setTimeout(() => {
        setIsSubmitting(false);
        setSnackbar({
          open: true,
          message: "Thank you! Your message has been sent successfully.",
          severity: "success",
        });
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          phone: "",
          subject: "",
          message: "",
        });
      }, 1500);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <>
      <Header />
      <Box
        sx={{
          background: "linear-gradient(180deg, #F7F8F2 0%, #e8efe6 100%)",
          minHeight: "100vh",
        }}
      >
        {/* Hero Section */}
        <Box
          className="contact-hero"
          sx={{
            pt: { xs: 10, md: 14 },
            pb: { xs: 6, md: 8 },
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* Animated Background Decorations */}
          <Box
            sx={{
              position: "absolute",
              top: -150,
              right: -150,
              width: 500,
              height: 500,
              borderRadius: "50%",
              background:
                "radial-gradient(circle, rgba(43, 173, 101, 0.05) 0%, transparent 70%)",
              zIndex: 0,
              animation: "pulse 8s ease-in-out infinite",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              bottom: -150,
              left: -150,
              width: 500,
              height: 500,
              borderRadius: "50%",
              background:
                "radial-gradient(circle, rgba(160, 239, 72, 0.04) 0%, transparent 70%)",
              zIndex: 0,
              animation: "pulse 8s ease-in-out infinite 2s",
            }}
          />

          {/* Floating Particles */}
          {[...Array(5)].map((_, i) => (
            <Box
              key={i}
              sx={{
                position: "absolute",
                top: `${10 + i * 20}%`,
                left: `${5 + (i % 4) * 25}%`,
                width: `${4 + i * 2}px`,
                height: `${4 + i * 2}px`,
                borderRadius: "50%",
                backgroundColor:
                  i % 2 === 0
                    ? "rgba(43, 173, 101, 0.12)"
                    : "rgba(160, 239, 72, 0.12)",
                animation: `float ${6 + i * 2}s ease-in-out infinite ${i * 1.5}s`,
                zIndex: 0,
              }}
            />
          ))}

          <Container maxWidth="xl" sx={{ position: "relative", zIndex: 1 }}>
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={6}>
                <Fade in timeout={800}>
                  <Box>
                    <Chip
                      label="📞 Contact Us"
                      sx={{
                        backgroundColor: "rgba(43, 173, 101, 0.1)",
                        color: "#2BAD65",
                        fontWeight: 600,
                        mb: 3,
                        px: 2,
                        border: "1px solid rgba(43, 173, 101, 0.15)",
                        "&:hover": {
                          backgroundColor: "rgba(43, 173, 101, 0.15)",
                        },
                        transition: "all 0.3s ease",
                      }}
                    />

                    <Typography
                      variant="h1"
                      component="h1"
                      sx={{
                        fontWeight: 800,
                        fontSize: { xs: "2.5rem", sm: "3.5rem", md: "4.5rem" },
                        color: "#1a1a1a",
                        mb: 2,
                        lineHeight: 1.1,
                      }}
                    >
                      How May We
                      <Box
                        component="span"
                        sx={{
                          display: "block",
                          background:
                            "linear-gradient(135deg, #2BAD65, #A0EF48, #2BAD65)",
                          backgroundSize: "200% 200%",
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent",
                          animation: "gradientMove 4s ease-in-out infinite",
                        }}
                      >
                        Help You?
                      </Box>
                    </Typography>

                    <Typography
                      variant="body1"
                      sx={{
                        color: "rgba(26, 26, 26, 0.7)",
                        fontSize: { xs: "1rem", md: "1.1rem" },
                        lineHeight: 1.8,
                        maxWidth: "90%",
                        mb: 3,
                      }}
                    >
                      Have questions or need assistance? We're here to help!
                      Reach out to us through any of the channels below.
                    </Typography>

                    {/* Trust Badges */}
                    <Stack
                      direction="row"
                      spacing={2}
                      sx={{ flexWrap: "wrap", gap: 1 }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <Verified sx={{ color: "#2BAD65", fontSize: 18 }} />
                        <Typography
                          variant="caption"
                          sx={{ color: "rgba(26, 26, 26, 0.4)" }}
                        >
                          24/7 Support
                        </Typography>
                      </Box>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <Star sx={{ color: "#FFD93D", fontSize: 18 }} />
                        <Typography
                          variant="caption"
                          sx={{ color: "rgba(26, 26, 26, 0.4)" }}
                        >
                          4.9/5 Response Rate
                        </Typography>
                      </Box>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <Support sx={{ color: "#A0EF48", fontSize: 18 }} />
                        <Typography
                          variant="caption"
                          sx={{ color: "rgba(26, 26, 26, 0.4)" }}
                        >
                          Expert Team
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                </Fade>
              </Grid>

              <Grid item xs={12} md={6}>
                <Slide direction="left" in timeout={1000}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <Box
                      sx={{
                        position: "relative",
                        width: "100%",
                        maxWidth: 400,
                      }}
                    >
                      <Box
                        component="img"
                        src={Telephone}
                        alt="Contact Wavepalm Techub"
                        sx={{
                          width: "100%",
                          height: "auto",
                          filter: "drop-shadow(0 20px 40px rgba(0,0,0,0.06))",
                          transition: "transform 0.5s ease",
                          "&:hover": {
                            transform: "scale(1.03) rotate(-2deg)",
                          },
                        }}
                      />
                      {/* Animated Decorative Rings */}
                      <Box
                        sx={{
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -50%)",
                          width: "130%",
                          height: "130%",
                          borderRadius: "50%",
                          border: "2px solid rgba(43, 173, 101, 0.05)",
                          animation: "spin 20s linear infinite",
                          zIndex: 0,
                        }}
                      />
                      <Box
                        sx={{
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -50%)",
                          width: "150%",
                          height: "150%",
                          borderRadius: "50%",
                          border: "2px solid rgba(160, 239, 72, 0.03)",
                          animation: "spin 25s linear infinite reverse",
                          zIndex: 0,
                        }}
                      />
                    </Box>
                  </Box>
                </Slide>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* Contact Info Cards */}
        <Container
          maxWidth="xl"
          sx={{ mt: -4, mb: 6, position: "relative", zIndex: 2 }}
        >
          <Grid container spacing={3} justifyContent="center">
            {contactInfo.map((info, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Fade in timeout={600 + index * 100}>
                  <Paper
                    sx={{
                      p: 3,
                      textAlign: "center",
                      backgroundColor: "rgba(255, 255, 255, 0.6)",
                      backdropFilter: "blur(20px)",
                      border: "1px solid rgba(43, 173, 101, 0.06)",
                      borderRadius: 3,
                      transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                      "&:hover": {
                        transform: "translateY(-8px)",
                        boxShadow: `0 8px 30px ${info.color}15`,
                        borderColor: info.color,
                        backgroundColor: info.bgColor,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        backgroundColor: info.bgColor,
                        borderRadius: "50%",
                        width: 65,
                        height: 65,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        margin: "0 auto 16px",
                        color: info.color,
                        transition: "all 0.3s ease",
                        "&:hover": {
                          transform: "scale(1.1) rotate(10deg)",
                        },
                      }}
                    >
                      {info.icon}
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{ color: "#1a1a1a", fontWeight: 600, mb: 1 }}
                    >
                      {info.title}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ color: "#1a1a1a", fontWeight: 500 }}
                    >
                      {info.details}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: "rgba(26, 26, 26, 0.3)" }}
                    >
                      {info.sub}
                    </Typography>
                  </Paper>
                </Fade>
              </Grid>
            ))}
          </Grid>
        </Container>

        {/* FAQ & Contact Form Section */}
        <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
          <Grid container spacing={4}>
            {/* FAQ Section */}
            <Grid item xs={12} md={6}>
              <Fade in timeout={800}>
                <Box>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      color: "#1a1a1a",
                      mb: 1,
                      fontSize: { xs: "1.8rem", md: "2.2rem" },
                    }}
                  >
                    Frequently Asked
                    <Box
                      component="span"
                      sx={{
                        display: "block",
                        background:
                          "linear-gradient(135deg, #2BAD65, #A0EF48, #2BAD65)",
                        backgroundSize: "200% 200%",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        animation: "gradientMove 4s ease-in-out infinite",
                      }}
                    >
                      Questions
                    </Box>
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "rgba(26, 26, 26, 0.4)",
                      mb: 3,
                    }}
                  >
                    Find answers to the most common questions our students ask
                  </Typography>

                  {faqs.map((faq, index) => (
                    <Fade in timeout={600 + index * 100} key={index}>
                      <Accordion
                        sx={{
                          backgroundColor: "rgba(255, 255, 255, 0.4)",
                          border: "1px solid rgba(43, 173, 101, 0.06)",
                          borderRadius: 2,
                          mb: 2,
                          "&:before": { display: "none" },
                          "&.Mui-expanded": {
                            borderColor: "#2BAD65",
                            boxShadow: "0 4px 20px rgba(43, 173, 101, 0.06)",
                          },
                          transition: "all 0.3s ease",
                        }}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMore sx={{ color: "#2BAD65" }} />}
                          sx={{
                            "& .MuiAccordionSummary-content": {
                              color: "#1a1a1a",
                              fontWeight: 500,
                            },
                            "&:hover": {
                              backgroundColor: "rgba(43, 173, 101, 0.03)",
                            },
                          }}
                        >
                          {faq.question}
                        </AccordionSummary>
                        <AccordionDetails
                          sx={{
                            color: "rgba(26, 26, 26, 0.5)",
                            lineHeight: 1.8,
                          }}
                        >
                          {faq.answer}
                        </AccordionDetails>
                      </Accordion>
                    </Fade>
                  ))}
                </Box>
              </Fade>
            </Grid>

            {/* Contact Form Section */}
            <Grid item xs={12} md={6}>
              <Fade in timeout={1000}>
                <Paper
                  component="form"
                  onSubmit={handleSubmit}
                  sx={{
                    p: { xs: 3, md: 4 },
                    backgroundColor: "rgba(255, 255, 255, 0.6)",
                    backdropFilter: "blur(20px)",
                    border: "1px solid rgba(43, 173, 101, 0.06)",
                    borderRadius: 3,
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  {/* Animated Border */}
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      height: 3,
                      background:
                        "linear-gradient(90deg, #2BAD65, #A0EF48, #2BAD65)",
                      backgroundSize: "200% 100%",
                      animation: "shimmer 3s linear infinite",
                    }}
                  />

                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 700,
                        color: "#1a1a1a",
                        mb: 1,
                      }}
                    >
                      Still Have a Question?
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "rgba(26, 26, 26, 0.4)" }}
                    >
                      Send us a message and we'll get back to you within 24
                      hours
                    </Typography>
                  </Box>

                  <Divider
                    sx={{ mb: 3, backgroundColor: "rgba(43, 173, 101, 0.06)" }}
                  />

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        name="firstName"
                        label="First Name"
                        value={formData.firstName}
                        onChange={handleChange}
                        error={!!errors.firstName}
                        helperText={errors.firstName}
                        InputProps={{
                          startAdornment: (
                            <Person
                              sx={{ mr: 1, color: "rgba(26,26,26,0.2)" }}
                            />
                          ),
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: "rgba(43, 173, 101, 0.04)",
                            color: "#1a1a1a",
                            borderRadius: 2,
                            "& fieldset": {
                              borderColor: "rgba(43, 173, 101, 0.08)",
                            },
                            "&:hover fieldset": {
                              borderColor: "rgba(43, 173, 101, 0.15)",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "#2BAD65",
                            },
                          },
                          "& .MuiInputLabel-root": {
                            color: "rgba(26, 26, 26, 0.4)",
                          },
                          "& .MuiFormHelperText-root": {
                            color: "#FF6B6B",
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        name="lastName"
                        label="Last Name"
                        value={formData.lastName}
                        onChange={handleChange}
                        error={!!errors.lastName}
                        helperText={errors.lastName}
                        InputProps={{
                          startAdornment: (
                            <Person
                              sx={{ mr: 1, color: "rgba(26,26,26,0.2)" }}
                            />
                          ),
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: "rgba(43, 173, 101, 0.04)",
                            color: "#1a1a1a",
                            borderRadius: 2,
                            "& fieldset": {
                              borderColor: "rgba(43, 173, 101, 0.08)",
                            },
                            "&:hover fieldset": {
                              borderColor: "rgba(43, 173, 101, 0.15)",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "#2BAD65",
                            },
                          },
                          "& .MuiInputLabel-root": {
                            color: "rgba(26, 26, 26, 0.4)",
                          },
                          "& .MuiFormHelperText-root": {
                            color: "#FF6B6B",
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        name="email"
                        label="Email Address"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        error={!!errors.email}
                        helperText={errors.email}
                        InputProps={{
                          startAdornment: (
                            <Email
                              sx={{ mr: 1, color: "rgba(26,26,26,0.2)" }}
                            />
                          ),
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: "rgba(43, 173, 101, 0.04)",
                            color: "#1a1a1a",
                            borderRadius: 2,
                            "& fieldset": {
                              borderColor: "rgba(43, 173, 101, 0.08)",
                            },
                            "&:hover fieldset": {
                              borderColor: "rgba(43, 173, 101, 0.15)",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "#2BAD65",
                            },
                          },
                          "& .MuiInputLabel-root": {
                            color: "rgba(26, 26, 26, 0.4)",
                          },
                          "& .MuiFormHelperText-root": {
                            color: "#FF6B6B",
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        name="phone"
                        label="Phone Number"
                        value={formData.phone}
                        onChange={handleChange}
                        error={!!errors.phone}
                        helperText={errors.phone}
                        InputProps={{
                          startAdornment: (
                            <Phone
                              sx={{ mr: 1, color: "rgba(26,26,26,0.2)" }}
                            />
                          ),
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: "rgba(43, 173, 101, 0.04)",
                            color: "#1a1a1a",
                            borderRadius: 2,
                            "& fieldset": {
                              borderColor: "rgba(43, 173, 101, 0.08)",
                            },
                            "&:hover fieldset": {
                              borderColor: "rgba(43, 173, 101, 0.15)",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "#2BAD65",
                            },
                          },
                          "& .MuiInputLabel-root": {
                            color: "rgba(26, 26, 26, 0.4)",
                          },
                          "& .MuiFormHelperText-root": {
                            color: "#FF6B6B",
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        name="subject"
                        label="Subject"
                        value={formData.subject}
                        onChange={handleChange}
                        error={!!errors.subject}
                        helperText={errors.subject}
                        InputProps={{
                          startAdornment: (
                            <Business
                              sx={{ mr: 1, color: "rgba(26,26,26,0.2)" }}
                            />
                          ),
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: "rgba(43, 173, 101, 0.04)",
                            color: "#1a1a1a",
                            borderRadius: 2,
                            "& fieldset": {
                              borderColor: "rgba(43, 173, 101, 0.08)",
                            },
                            "&:hover fieldset": {
                              borderColor: "rgba(43, 173, 101, 0.15)",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "#2BAD65",
                            },
                          },
                          "& .MuiInputLabel-root": {
                            color: "rgba(26, 26, 26, 0.4)",
                          },
                          "& .MuiFormHelperText-root": {
                            color: "#FF6B6B",
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        name="message"
                        label="Message"
                        multiline
                        rows={4}
                        value={formData.message}
                        onChange={handleChange}
                        error={!!errors.message}
                        helperText={errors.message}
                        InputProps={{
                          startAdornment: (
                            <Message
                              sx={{ mr: 1, color: "rgba(26,26,26,0.2)" }}
                            />
                          ),
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: "rgba(43, 173, 101, 0.04)",
                            color: "#1a1a1a",
                            borderRadius: 2,
                            "& fieldset": {
                              borderColor: "rgba(43, 173, 101, 0.08)",
                            },
                            "&:hover fieldset": {
                              borderColor: "rgba(43, 173, 101, 0.15)",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "#2BAD65",
                            },
                          },
                          "& .MuiInputLabel-root": {
                            color: "rgba(26, 26, 26, 0.4)",
                          },
                          "& .MuiFormHelperText-root": {
                            color: "#FF6B6B",
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        type="submit"
                        variant="contained"
                        fullWidth
                        disabled={isSubmitting}
                        startIcon={!isSubmitting ? <RocketLaunch /> : null}
                        sx={{
                          py: 1.5,
                          fontSize: "1rem",
                          fontWeight: 700,
                          background:
                            "linear-gradient(135deg, #2BAD65, #A0EF48)",
                          borderRadius: 2,
                          color: "#ffffff",
                          boxShadow: "0 4px 25px rgba(43, 173, 101, 0.3)",
                          "&:hover": {
                            transform: "translateY(-3px) scale(1.02)",
                            boxShadow: "0 8px 35px rgba(43, 173, 101, 0.4)",
                            background:
                              "linear-gradient(135deg, #239A56, #8EDB40)",
                          },
                          "&:disabled": {
                            backgroundColor: "rgba(43, 173, 101, 0.15)",
                          },
                          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        }}
                      >
                        {isSubmitting ? (
                          <>
                            <CircularProgress
                              size={24}
                              sx={{ mr: 1, color: "#ffffff" }}
                            />
                            Sending...
                          </>
                        ) : (
                          "Send Message"
                        )}
                      </Button>
                    </Grid>
                  </Grid>
                </Paper>
              </Fade>
            </Grid>
          </Grid>
        </Container>
      </Box>
      <Footer />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          icon={<CheckCircle fontSize="inherit" />}
          sx={{
            backgroundColor: "#ffffff",
            color: "#1a1a1a",
            border: "1px solid rgba(43, 173, 101, 0.08)",
            "& .MuiAlert-icon": {
              color: "#2BAD65",
            },
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <style>
        {`
          @keyframes pulse {
            0%, 100% { transform: scale(1); opacity: 0.3; }
            50% { transform: scale(1.1); opacity: 0.6; }
          }

          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-10px); }
          }

          @keyframes spin {
            0% { transform: translate(-50%, -50%) rotate(0deg); }
            100% { transform: translate(-50%, -50%) rotate(360deg); }
          }

          @keyframes gradientMove {
            0%, 100% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
          }

          @keyframes shimmer {
            0% { background-position: -200% 0; }
            100% { background-position: 200% 0; }
          }
        `}
      </style>
    </>
  );
};

export default ContactUs;
